/* Learning Time quiz: logic layer (pure functions, no DOM, no copy).
   Loaded as a plain <script defer> in the theme and as CommonJS in tests.
   Screen renderers (R, render) live here too, then the browser controller (mount).
   v2.1: cover screen (intro) before question 1; "n dari 8" counter on question screens.
   v2.2: cover with photo, teaser chips and a parent quote (cover copy and photo come from
   the section settings via data.intro, so the team can edit them in the theme editor).
   v2.3: expecting parents (goal "hamil") get their own wording wherever a "<id>.hamil"
   content id exists, and skip the screen-time question (counter shows 7); benefit and
   support lists show every numbered id that exists. */
(function () {
  "use strict";

  // Structure only. Copy for each stage lives in assets/lt-quiz-content.json (built from content/lt-quiz-content.src.json).
  // tools[i].area: the area tool i+1 serves on the mech screen; areas with no tool are
  // trained through the parent guide. Each stage has one skill per area (stage.<id>.skill.<area>).
  var STAGES = [
    { id: "sensory",      min: 0,  max: 3,  tools: [{ area: "fisik" }, { area: "kognitif" }] },
    { id: "explorer",     min: 4,  max: 6,  tools: [{ area: "fisik" }, { area: "kognitif" }] },
    { id: "mover",        min: 7,  max: 9,  tools: [{ area: "fisik" }, { area: "kognitif" }] },
    { id: "thinker",      min: 10, max: 12, tools: [{ area: "kognitif" }, { area: "sosem" }] },
    { id: "inspector",    min: 13, max: 15, tools: [{ area: "fisik" }, { area: "sosem" }] },
    { id: "finemotor",    min: 16, max: 18, tools: [{ area: "fisik" }, { area: "kognitif" }] },
    { id: "builder",      min: 19, max: 21, tools: [{ area: "fisik" }, { area: "kognitif" }] },
    { id: "investigator", min: 22, max: 24, tools: [{ area: "bahasa" }, { area: "sosem" }] },
    { id: "observer",     min: 25, max: 27, tools: [{ area: "sosem" }, { area: "kognitif" }] },
    { id: "adventurer",   min: 28, max: 30, tools: [{ area: "sosem" }, { area: "bahasa" }] }
  ];

  // Area question order; index AREA_KEYS.length is "all" (area.semua).
  var AREA_KEYS = ["fisik", "bahasa", "sosem", "kognitif"];
  var GOAL_KEYS = ["tahu", "cari", "siap", "hamil", "lain"];
  var CONF_KEYS = ["yakin", "ragu", "bingung"];
  var HOPE_KEYS = ["tahap", "ide", "bonding", "yakin", "semua"];
  var WHO_KEYS = ["sendiri", "pasangan", "kakeknenek", "pengasuh"];
  var SCREEN_KEYS = ["tidak", "hampir", "kurang1", "1-2", "lebih2"];
  var TIME_KEYS = ["kurang15", "15-30", "lebih30"];

  var FLOW = ["intro", "goal", "dob", "area", "conf", "hope", "ch1", "who", "screen", "factors", "ch2", "time", "loading", "recap", "mech", "proof", "reviews", "support", "gate", "result"];
  var QUESTION_SCREENS = ["goal", "dob", "area", "conf", "hope", "who", "screen", "time"];
  var INTER_SCREENS = ["ch1", "ch2", "loading"];
  // Questions that do not apply before birth: skipped for expecting parents.
  var SKIP_IF_HAMIL = ["screen"];

  var DAY_MS = 864e5;

  // Local calendar date at midnight, so time of day never shifts a day count.
  function dateOnly(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function parseIso(iso) {
    return new Date(iso + "T00:00:00");
  }

  function addMonths(iso, m) {
    var x = parseIso(iso);
    x.setMonth(x.getMonth() + m);
    return x;
  }

  function monthsOld(isoDob, today) {
    var b = parseIso(isoDob);
    var m = (today.getFullYear() - b.getFullYear()) * 12 + (today.getMonth() - b.getMonth());
    if (today.getDate() < b.getDate()) m--;
    return m;
  }

  function weeksFrom(daysLeft) {
    return Math.max(1, Math.round(daysLeft / 7));
  }

  function computeStage(answers, today) {
    var now = dateOnly(today);
    var dob = parseIso(answers.dob);
    var daysLeft;

    if (answers.goal === "hamil") {
      if (dob < now) return { error: "due_past" };
      daysLeft = Math.max(0, Math.round((dob - now) / DAY_MS));
      return { months: null, curIdx: 0, recIdx: 0, last: false, daysLeft: daysLeft, weeksLeft: weeksFrom(daysLeft) };
    }

    if (dob > now) return { error: "future" };
    var months = monthsOld(answers.dob, now);
    var curIdx = -1;
    for (var i = 0; i < STAGES.length; i++) {
      if (months >= STAGES[i].min && months <= STAGES[i].max) { curIdx = i; break; }
    }
    if (curIdx < 0) return { error: "over30" };

    var last = months === STAGES[curIdx].max && curIdx < STAGES.length - 1;
    var recIdx = last ? curIdx + 1 : curIdx;
    var end = addMonths(answers.dob, STAGES[curIdx].max + 1);
    daysLeft = Math.max(0, Math.round((end - now) / DAY_MS));
    return { months: months, curIdx: curIdx, recIdx: recIdx, last: last, daysLeft: daysLeft, weeksLeft: weeksFrom(daysLeft) };
  }

  function nextStep(current, ctx) {
    var i = FLOW.indexOf(current) + 1;
    while (i < FLOW.length) {
      var key = FLOW[i];
      var skipGate = key === "gate" && !(ctx.variant === "B" && !ctx.leadCaptured);
      var skipReviews = key === "reviews" && ctx.reviewCount < 2;
      var skipHamil = !!ctx.hamil && SKIP_IF_HAMIL.indexOf(key) !== -1;
      if (!skipGate && !skipReviews && !skipHamil) return key;
      i++;
    }
    return current;
  }

  // The question screens this parent will see, in order (for the "n dari N" counter).
  function questionsFor(hamil) {
    return hamil ? QUESTION_SCREENS.filter(function (q) { return SKIP_IF_HAMIL.indexOf(q) === -1; }) : QUESTION_SCREENS;
  }

  function backStep(history) {
    var rest = history.slice(0, -1);
    while (rest.length && (INTER_SCREENS.indexOf(rest[rest.length - 1]) !== -1 || rest[rest.length - 1] === "gate")) {
      rest.pop();
    }
    if (!rest.length) {
      var first = history.length ? history[0] : FLOW[0];
      return { step: first, history: [first] };
    }
    return { step: rest[rest.length - 1], history: rest };
  }

  // Browser-history wiring for go(): null on the first screen (the browser's own back
  // leaves the page), "replace" after a chapter, the loader or the gate (back skips
  // those, so they must not keep an entry), else "push". One entry per back target.
  function entryMode(history) {
    if (history.length < 2) return null;
    var prev = history[history.length - 2];
    return INTER_SCREENS.indexOf(prev) !== -1 || prev === "gate" ? "replace" : "push";
  }

  // What back (button or gesture) does from here: a backStep result, or null on the first screen.
  function backTarget(history) {
    return history.length < 2 ? null : backStep(history);
  }

  // The gate takes a WhatsApp number only. Anything else, emails included, is rejected.
  function normaliseContact(raw) {
    if (typeof raw !== "string") return null;
    var s = raw.trim();
    if (/[^\d\s+\-().]/.test(s)) return null; // letters or "@" mean it is not a phone number
    var digits = s.replace(/[^\d+]/g, "");
    var m = /^(?:\+?62|0)?(8\d{8,11})$/.exec(digits);
    return m ? { method: "whatsapp", contact: "+62" + m[1] } : null;
  }

  // The tracked answer for the area question: the area key, or "semua" for "all".
  function areaValue(i) { return AREA_KEYS[i] || "semua"; }

  function fmt(template, vars) {
    return String(template).replace(/\{(\w+)\}/g, function (token, name) {
      return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : token;
    });
  }

  // ---------------------------------------------------------------------------
  // Renderers. Pure: (state, data, C) -> HTML string. Every parent-facing string
  // comes from C(contentId); every interpolated value goes through esc().
  // ---------------------------------------------------------------------------

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  // Lookup over the fetched content asset, a flat {id: text} map holding only filled ids.
  // Reviews (review.*) are optional: absent means not filled, "".
  // Any other absent id fails loudly in tests (module exists); the browser logs it and
  // renders nothing rather than breaking the quiz. C.has(id) tells whether an id exists.
  var OPTIONAL_ID = /^review\./;
  function makeC(items) {
    var has = function (id) { return !!items && Object.prototype.hasOwnProperty.call(items, id) && typeof items[id] === "string"; };
    var C = function (id) {
      if (has(id)) return items[id];
      if (OPTIONAL_ID.test(id)) return "";
      if (typeof module !== "undefined" && module.exports) throw new Error("missing content: " + id);
      if (typeof console !== "undefined") console.warn("missing content: " + id);
      return "";
    };
    C.has = has;
    return C;
  }

  // The expecting-parent version of a content id when the parent is expecting and one exists.
  function V(state, C, id) {
    return state.goal === "hamil" && C.has && C.has(id + ".hamil") ? id + ".hamil" : id;
  }

  // Every numbered id that exists: prefix1, prefix2, ... up to max.
  function listOf(C, prefix, max) {
    var out = [];
    for (var n = 1; n <= max; n++) { if (!C.has || C.has(prefix + n)) out.push(C(prefix + n)); }
    return out;
  }

  var AREA_ICONS = {
    fisik: '<circle cx="7" cy="5" r="2.2"/><path d="M4 21l3-7 3 3v5M7 14l1-5 5 3 4-2"/>',
    bahasa: '<path d="M4 5h16v10H9l-5 4z"/><path d="M8 9h8M8 12h5"/>',
    sosem: '<circle cx="12" cy="12" r="8.5"/><path d="M8.5 14.5a4.5 4.5 0 007 0"/><circle cx="9" cy="10" r=".8"/><circle cx="15" cy="10" r=".8"/>',
    kognitif: '<path d="M9 18h6M10 21h4M12 3a6 6 0 00-3.5 10.9V16h7v-2.1A6 6 0 0012 3z"/>',
    semua: '<path d="M12 4v16M4 12h16"/>'
  };

  function svg(p) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + p + "</svg>";
  }

  function hl(v) { return { hl: v }; }

  // Content template -> HTML. The template and every value are escaped; "\n" becomes <br>.
  function T(C, id, vars) {
    var safe = {};
    Object.keys(vars || {}).forEach(function (k) {
      var v = vars[k];
      safe[k] = v !== null && typeof v === "object" ? '<span class="hl">' + esc(v.hl) + "</span>" : esc(v);
    });
    return fmt(esc(C(id)), safe).replace(/\n/g, "<br>");
  }

  function stageName(C, s) { return C("stage." + s.id + ".name"); }
  function stageAnchor(C, s) { return C("stage." + s.id + ".anchor"); }
  // Token set for templates: names ({stage}, {next}) follow "paket", anchors ({tahap}, {tahapNext}) follow "tahap".
  function stageVars(C, s, nextS) {
    var v = { stage: stageName(C, s), tahap: stageAnchor(C, s) };
    if (nextS) { v.next = stageName(C, nextS); v.tahapNext = stageAnchor(C, nextS); }
    return v;
  }
  function stageRange(C, s) { return fmt(C("ui.range"), { min: s.min, max: s.max }); }
  function ageText(C, state) {
    return state.goal === "hamil" ? C("ui.age.unborn") : fmt(C("ui.age.months"), { m: state.stage.months });
  }
  function lower(s) { return String(s).toLowerCase(); }
  function areaKey(state) { return typeof state.area === "number" && state.area < AREA_KEYS.length ? AREA_KEYS[state.area] : null; }
  // Section data for one stage's kit: title, price (Shopify money text), price_amount
  // (whole rupiah), compare_at, image, variant_id, url. {} when the picker is empty.
  function kitOf(data, s) {
    return (data && data.stages && data.stages[s.id]) || {};
  }

  // Whole rupiah with id-ID thousands dots (1498000 -> "1.498.000"), through ui.money.
  function money(C, n) {
    return fmt(C("ui.money"), { amount: String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".") });
  }

  // The two-kit option on the result: this kit plus the next stage's. null when there is
  // no next stage (adventurer) or either kit lacks a number price or variant. The store's
  // automatic discount (3% from two items) applies at checkout; saving shows that amount.
  function bundleOffer(state, data) {
    var i = state.stage.recIdx, n = STAGES[i + 1];
    if (!n) return null;
    var a = kitOf(data, STAGES[i]), b = kitOf(data, n);
    if (typeof a.price_amount !== "number" || typeof b.price_amount !== "number" || !a.variant_id || !b.variant_id) return null;
    var total = a.price_amount + b.price_amount;
    return { next: n, total: total, saving: Math.round(total * 0.03) };
  }

  // The price line for the selected option: bundle total, or the kit price with compare-at.
  function priceBlock(state, data, C) {
    var kit = kitOf(data, STAGES[state.stage.recIdx]), b = state.bundle ? bundleOffer(state, data) : null;
    if (b) return "<strong>" + esc(money(C, b.total)) + "</strong>";
    return "<strong>" + esc(kit.price) + "</strong>" + (kit.compare_at ? "<s>" + esc(kit.compare_at) + "</s>" : "");
  }

  function offerCards(state, data, C) {
    var b = bundleOffer(state, data);
    if (!b) return "";
    var s = STAGES[state.stage.recIdx], kit = kitOf(data, s), on = !!state.bundle;
    var sv = stageVars(C, s, b.next);
    var card = function (key, checked, inner) {
      return '<button type="button" class="pick" role="radio" aria-checked="' + checked + '" data-offer="' + key + '">' + inner + "</button>";
    };
    return '<div class="picks" role="radiogroup" aria-label="' + esc(kit.title) + '">' +
      card("single", !on, '<span class="t">' + T(C, "result.offer.single", sv) + '</span><span class="amt">' + esc(kit.price) + "</span>") +
      card("bundle", on, '<span class="t">' + T(C, "result.offer.bundle", sv) + '</span><span class="amt">' + esc(money(C, b.total)) + "</span>" +
        '<span class="save">' + T(C, "result.offer.saving", { saving: money(C, b.saving) }) + "</span>" +
        '<span class="ahead">' + T(C, "result.offer.bundle.why", sv) + "</span>") +
      "</div>";
  }

  // What the cart gets: the kit, plus the next stage's kit when the bundle is chosen and
  // offered, and the order attributes (the session id is added at checkout).
  function cartPayload(state, data, bundle) {
    var s = STAGES[state.stage.recIdx], b = bundle ? bundleOffer(state, data) : null;
    var items = [{ id: kitOf(data, s).variant_id, quantity: 1 }];
    if (b) items.push({ id: kitOf(data, b.next).variant_id, quantity: 1 });
    return { items: items, attributes: {
      _lt_quiz_variant: state.variant, _lt_quiz_stage: s.id, _lt_quiz_goal: state.goal, _lt_quiz_bundle: b ? "2" : "1"
    } };
  }

  function trimmed(v) { return String(v == null ? "" : v).trim(); }

  // The time-left bar on the result: fill is the share of the 13-week stage already gone,
  // clamped to [0.08, 1]; in the last month it stays on that scale but at least two-thirds
  // full. hot (coral) when four weeks or less remain or in the last month. Expecting
  // parents (months === null) get no bar, only the countdown line.
  function urgency(st) {
    if (!st || st.months === null || st.months === undefined) return null;
    var clamp = function (x) { return Math.max(0.08, Math.min(1, x)); };
    if (st.last) return { fill: Math.max(0.66, Math.min(1, (13 - st.daysLeft / 7) / 13)), hot: true };
    return { fill: clamp((13 - st.weeksLeft) / 13), hot: st.weeksLeft <= 4 };
  }

  // Reviews from review.<stage>.<n>.* (n = 1..4); only those with a quote count.
  // Photos are Shopify Files names, prefixed with the section's img_base.
  function stageReviews(stageId, C, imgBase) {
    var out = [];
    for (var n = 1; n <= 4; n++) {
      var k = "review." + stageId + "." + n + ".";
      var quote = trimmed(C(k + "quote"));
      if (!quote) continue;
      var photo = trimmed(C(k + "photo"));
      var base = imgBase ? String(imgBase).replace(/\/*$/, "/") : "";
      out.push({ quote: quote, name: trimmed(C(k + "name")), age: trimmed(C(k + "age")),
        verified: trimmed(C(k + "verified")) === "ya", photo: photo ? base + photo : "" });
    }
    return out;
  }

  function why(C, bodyId, srcId) {
    return '<div class="why"><b>' + esc(C("ui.why.label")) + "</b>" + esc(C(bodyId)) +
      (srcId ? '<div class="src">' + esc(C(srcId)) + "</div>" : "") + "</div>";
  }

  // One tappable answer. value is the answer key (the area index for the area question).
  function option(group, value, pressed, title, desc, icon) {
    return '<button type="button" class="opt" aria-pressed="' + pressed + '" data-single="' + group + ":" + esc(value) + '">' +
      (icon ? '<span class="ico">' + svg(icon) + "</span>" : "") +
      "<span><span" + (desc ? ' class="t"' : ' class="t" style="font-weight:400"') + ">" + esc(title) + "</span>" +
      (desc ? '<span class="d">' + esc(desc) + "</span>" : "") + "</span></button>";
  }

  function chooser(group, state, keys, C, idOf) {
    return '<div class="opts">' + keys.map(function (k) {
      return option(group, k, state[group] === k, C(idOf(k)));
    }).join("") + "</div>";
  }

  var R = {
    // Cover screen: a photo with two teaser chips (what the quiz will tell them), how short
    // it is, the promise, three things they get, the start button and one parent quote.
    // Photo, chips, eyebrow and quote come from the section settings (data.intro).
    intro: function (state, data, C) {
      var d = (data && data.intro) || {};
      var base = data && data.img_base ? String(data.img_base).replace(/\/*$/, "/") : "";
      var chip = function (cls, t) {
        return trimmed(t) ? '<span class="chip ' + cls + '"><i aria-hidden="true">?</i>' + esc(t) + "</span>" : "";
      };
      var quote = trimmed(d.quote)
        ? '<figure class="quote">' + (trimmed(d.quote_photo) ? '<img src="' + esc(base + trimmed(d.quote_photo)) + '" alt="" loading="lazy">' : "") +
          '<figcaption><span class="q">' + esc(d.quote) + "</span><br><b>" + esc(d.quote_name) + "</b>" +
          (trimmed(d.quote_program) ? '<span class="who">, ' + esc(d.quote_program) + "</span>" : "") + "</figcaption></figure>"
        : "";
      return '<div class="intro"><div class="intro-photo">' + chip("c1", d.chip1) + chip("c2", d.chip2) + "</div>" +
        (trimmed(d.eyebrow) ? '<p class="eyebrow">' + esc(d.eyebrow) + "</p>" : "") +
        "<h1>" + esc(C("intro.headline")) + "</h1>" +
        '<ul class="ticks">' + [1, 2, 3].map(function (n) { return "<li>" + esc(C("intro.point" + n)) + "</li>"; }).join("") + "</ul>" +
        '<button class="cta" data-next>' + esc(C("intro.cta")) + "</button>" + quote + "</div>";
    },

    goal: function (state, data, C) {
      return "<h1>" + esc(C("q.goal.stem")) + "</h1>" +
        chooser("goal", state, GOAL_KEYS, C, function (k) { return "q.goal.opt." + k; }) +
        '<p class="disclaimer">' + esc(C("q.goal.disclaimer")) + "</p>";
    },

    dob: function (state, data, C) {
      var preg = state.goal === "hamil";
      return "<h1>" + esc(C(preg ? "q.dob.stem.hamil" : "q.dob.stem")) + "</h1>" +
        '<input type="date" id="dob" value="' + esc(state.dob) + '" aria-label="' + esc(C(preg ? "q.dob.aria.hamil" : "q.dob.aria")) + '">' +
        '<div class="err" id="err"></div>' +
        '<button class="cta" id="dobNext">' + esc(C("ui.nav.next")) + "</button>" +
        '<div class="spacer"></div>' + why(C, "why.dob");
    },

    area: function (state, data, C) {
      var keys = AREA_KEYS.concat("semua");
      return "<h1>" + esc(C(V(state, C, "q.area.stem"))) + "</h1>" +
        '<div class="opts">' + keys.map(function (k, i) {
          return option("area", i, state.area === i, C("area." + k + ".t"), C("area." + k + ".d"), AREA_ICONS[k]);
        }).join("") + "</div>";
    },

    conf: function (state, data, C) {
      return "<h1>" + esc(C(V(state, C, "q.conf.stem"))) + "</h1>" +
        '<div class="opts">' + CONF_KEYS.map(function (k) {
          return option("conf", k, state.conf === k, C(V(state, C, "q.conf.opt." + k + ".t")), C(V(state, C, "q.conf.opt." + k + ".d")));
        }).join("") + "</div>";
    },

    hope: function (state, data, C) {
      var picked = state.hopes || [];
      return "<h1>" + esc(C(V(state, C, "q.hope.stem"))) + "</h1>" +
        '<p class="sub">' + esc(C("q.hope.sub")) + "</p>" +
        '<div class="opts">' + HOPE_KEYS.map(function (k) {
          return '<button type="button" class="opt" role="checkbox" aria-checked="' + (picked.indexOf(k) !== -1) + '" data-hope="' + k + '">' +
            '<span class="t" style="font-weight:400">' + esc(C("q.hope.opt." + k)) + '</span><span class="box" aria-hidden="true"></span></button>';
        }).join("") + "</div>" +
        '<button class="cta" id="hopeNext"' + (picked.length ? "" : " disabled") + ">" + esc(C("ui.nav.next")) + "</button>";
    },

    ch1: function (state, data, C) {
      return '<p class="inter-text">' + esc(C("ch1.line1")) + '</p><p class="inter-text dim">' + esc(C("ch1.line2")) + "</p>";
    },

    who: function (state, data, C) {
      return "<h1>" + esc(C(V(state, C, "q.who.stem"))) + "</h1>" +
        chooser("who", state, WHO_KEYS, C, function (k) { return "q.who.opt." + k; }) +
        '<div class="spacer"></div>' + why(C, "why.who");
    },

    screen: function (state, data, C) {
      return "<h1>" + esc(C("q.screen.stem")) + "</h1>" +
        chooser("screen", state, SCREEN_KEYS, C, function (k) { return "q.screen.opt." + k; }) +
        '<div class="spacer"></div>' + why(C, "why.screen", "why.screen.src");
    },

    factors: function (state, data, C) {
      var key = areaKey(state);
      var area = key ? lower(C("area." + key + ".t")) : C("factors.area.all");
      var weeks = state.goal === "hamil" ? "" : " " + T(C, "factors.weeks", { w: hl(state.stage.weeksLeft) });
      return '<p class="inter-text">' + T(C, V(state, C, "factors.line1"), { age: hl(ageText(C, state)), who: hl(lower(C("q.who.opt." + state.who))) }) + "</p>" +
        '<p class="inter-text dim">' + T(C, "factors.line2", { area: hl(area) }) + weeks + "</p>" +
        '<button class="pill" data-next>' + esc(C("ui.nav.next")) + "</button>";
    },

    ch2: function (state, data, C) {
      return '<p class="inter-text">' + esc(C("ch2.line1")) + '</p><p class="inter-text dim">' + esc(C("ch2.line2")) + "</p>";
    },

    time: function (state, data, C) {
      return "<h1>" + esc(C(V(state, C, "q.time.stem"))) + "</h1>" +
        '<p class="sub">' + esc(C("q.time.sub")) + "</p>" +
        chooser("time", state, TIME_KEYS, C, function (k) { return "q.time.opt." + k; });
    },

    loading: function (state, data, C) {
      var good = state.goal === "hamil" ? C("loading.good.hamil") : fmt(C("loading.good"), stageVars(C, STAGES[state.stage.recIdx]));
      return '<div class="loader"><div class="tiles" aria-hidden="true">' +
        '<div class="tile" style="left:0;top:60px;width:44%;height:130px"></div>' +
        '<div class="tile" style="right:4%;top:0;width:46%;height:120px"></div>' +
        '<div class="tile" style="right:16%;top:140px;width:36%;height:110px"></div></div>' +
        '<div class="lmsg" id="lmsg" data-good="' + esc(good) + '"><span>' + esc(C("loading.reviewing")) + "</span></div>" +
        '<div class="spin" aria-hidden="true"></div></div>';
    },

    recap: function (state, data, C) {
      var st = state.stage, hamil = state.goal === "hamil";
      var cur = STAGES[st.curIdx], rec = STAGES[st.recIdx];
      var key = areaKey(state);
      var areaV = key ? esc(C("area." + key + ".t")) : esc(C("recap.v.area.all"));
      var stageV = hamil
        ? T(C, "recap.v.stage.hamil", Object.assign({ age: ageText(C, state), range: stageRange(C, cur),
            first: fmt(C("ui.stage.first"), Object.assign({ range: stageRange(C, STAGES[0]) }, stageVars(C, STAGES[0]))) }, stageVars(C, cur)))
        : T(C, "recap.v.stage", Object.assign({ age: ageText(C, state), range: stageRange(C, cur) }, stageVars(C, cur)));
      var daily = T(C, V(state, C, "recap.v.daily"), {
        who: lower(C("q.who.opt." + state.who)),
        screen: state.screen ? lower(C("q.screen.opt." + state.screen)) : "",
        time: lower(C("q.time.opt." + state.time))
      });
      return "<h1>" + esc(C("recap.headline")) + "</h1>" +
        '<div class="recapcard"><div class="head">' + esc(C("recap.card.head")) + "</div>" +
        '<section><div class="lbl">' + esc(C("recap.lbl.stage")) + '</div><div class="v">' + stageV + "</div></section>" +
        '<section><div class="lbl">' + esc(C("recap.lbl.area")) + '</div><div class="v">' + areaV + "</div></section>" +
        '<section><div class="lbl">' + esc(C("recap.lbl.conf")) + '</div><div class="v">' + esc(C(V(state, C, "q.conf.opt." + state.conf + ".t"))) + "</div></section>" +
        '<section><div class="lbl">' + esc(C("recap.lbl.daily")) + '</div><div class="v">' + daily + "</div></section></div>" +
        (st.last
          ? '<p class="small" style="margin-top:14px;padding:12px 14px;border-left:3px solid var(--blue);background:var(--surface);border-radius:0 12px 12px 0">' +
            T(C, "recap.lastmonth", Object.assign({ days: st.daysLeft }, stageVars(C, rec))) + "</p>"
          : "") +
        '<p style="margin-top:18px">' + esc(C("recap.outro")) + "</p>" +
        '<button class="pill" data-next>' + esc(C("ui.nav.next")) + "</button>";
    },

    // One card per area, in AREA_KEYS order: this stage's skill for it, trained by the
    // stage's tool for that area or else by the guide. The chosen area is outlined.
    mech: function (state, data, C) {
      var rec = STAGES[state.stage.recIdx], key = areaKey(state);
      var nodes = AREA_KEYS.map(function (a) {
        var tools = [];
        rec.tools.forEach(function (t, j) { if (t.area === a) tools.push(C("stage." + rec.id + ".tool" + (j + 1) + ".name")); });
        var on = a === key;
        return '<div class="node' + (on ? " on" : "") + '">' +
          (on ? '<span class="tag">' + esc(C("mech.tag")) + "</span>" : "") +
          '<div class="area">' + esc(C("area." + a + ".t")) + "</div>" +
          '<div class="skill">' + esc(C("stage." + rec.id + ".skill." + a)) + "</div>" +
          '<div class="via"><i>' + esc(C(tools.length ? "mech.via.tool" : "mech.via.guide")) + "</i><span>" +
          esc(tools.length ? tools.join(", ") : C("mech.guide.text")) + "</span></div></div>";
      }).join("");
      // Same test as the cards: "tools" only when one of this stage's tools serves the area.
      var byTool = key && rec.tools.some(function (t) { return t.area === key; });
      var note = key ? '<p class="reassure">' + T(C, byTool ? "mech.reassure" : "mech.reassure.guide", { area: lower(C("area." + key + ".t")) }) + "</p>" : "";
      return '<div class="mech"><h1>' + T(C, "mech.headline", stageVars(C, rec)) + "</h1>" +
        '<div class="diagram">' + nodes + "</div>" + note +
        '<div class="spacer"></div><button class="pill" data-next>' + esc(C("mech.cta")) + "</button></div>";
    },

    proof: function (state, data, C) {
      var stats = [1, 2, 3].map(function (n) {
        return '<div class="stat"><b>' + esc(C("proof.stat" + n + ".n")) + "</b><span>" + esc(C("proof.stat" + n + ".t")) + "</span></div>";
      }).join("");
      return "<h1>" + esc(C("proof.headline")) + '</h1><div class="stats">' + stats + "</div>" +
        '<div class="spacer"></div><button class="pill" data-next>' + esc(C("proof.cta")) + "</button>";
    },

    reviews: function (state, data, C) {
      var rec = STAGES[state.stage.recIdx];
      var cards = stageReviews(rec.id, C, data && data.img_base).map(function (r) {
        var who = "<b>" + esc(r.name) + "</b>" +
          (r.age ? ", " + T(C, "reviews.childage", { m: r.age }) : "") +
          (r.verified ? "<br>" + esc(C("reviews.verified")) : "");
        return '<div class="tcard">' + (r.photo ? '<img class="rphoto" src="' + esc(r.photo) + '" alt="" loading="lazy">' : "") +
          '<p class="q">' + esc(r.quote) + '</p><div class="who">' + who + "</div></div>";
      }).join("");
      return "<h1>" + T(C, "reviews.headline", stageVars(C, rec)) + "</h1>" +
        '<div class="carousel">' + cards + "</div>" +
        '<p class="fine">' + esc(C("reviews.fine")) + "</p>" +
        '<div class="spacer"></div><button class="pill" data-next>' + esc(C("reviews.cta")) + "</button>";
    },

    support: function (state, data, C) {
      return '<div class="collage" aria-hidden="true"><div></div><div></div><div></div></div>' +
        "<h1>" + esc(C("support.headline")) + "</h1>" +
        '<ul class="ticks">' + listOf(C, "support.tick", 6).map(function (t) { return "<li>" + esc(t) + "</li>"; }).join("") + "</ul>" +
        '<div class="spacer"></div><button class="pill" data-next>' + esc(C("support.cta")) + "</button>";
    },

    gate: function (state, data, C) {
      return R.support(state, data, C) +
        '<div class="overlay"><div class="modal" role="dialog" aria-modal="true" aria-labelledby="gt">' +
        '<h1 id="gt">' + esc(C("gate.headline")) + "</h1>" +
        '<input type="tel" inputmode="tel" id="contact" placeholder="' + esc(C("gate.placeholder")) + '" aria-label="' + esc(C("gate.placeholder")) + '" autocomplete="tel">' +
        '<div class="err" id="gateErr"></div>' +
        '<button class="cta" id="gateNext">' + esc(C("gate.cta")) + "</button>" +
        '<p class="small sub" style="margin-top:14px;text-align:center">' + esc(C("gate.consent")) + "</p></div></div>";
    },

    result: function (state, data, C) {
      var st = state.stage, s = STAGES[st.recIdx], kit = kitOf(data, s);
      var line, u = state.goal === "hamil" ? null : urgency(st);
      if (state.goal === "hamil") line = esc(C("result.countdown.hamil"));
      else if (st.last) line = T(C, "result.countdown.last", Object.assign({ days: st.daysLeft }, stageVars(C, s)));
      else line = T(C, "result.countdown.mid", Object.assign({ m: st.months, w: st.weeksLeft }, stageVars(C, s)));
      var pct = u ? Math.round(u.fill * 100) : 0;
      var countdown = '<div class="urgency' + (u && u.hot ? " hot" : "") + '"><p>' + line + "</p>" +
        (u ? '<div class="ubar" role="progressbar" aria-label="' + esc(C("result.countdown.aria")) + '" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '"><i style="width:' + pct + '%"></i></div>' : "") +
        "</div>";
      return "<h1>" + esc(C("result.headline")) + "</h1>" +
        '<div class="offer">' +
        '<div class="ph">' + (kit.image ? '<img src="' + esc(kit.image) + '" alt="' + esc(kit.title) + '" loading="lazy">' : "") + "</div>" +
        '<div class="body"><h2>' + esc(kit.title) + "</h2>" +
        '<div class="sub small">' + esc(stageRange(C, s)) + "</div>" +
        '<p style="margin:10px 0 0">' + esc(C("stage." + s.id + ".promise")) + "</p>" +
        offerCards(state, data, C) +
        '<div class="price" id="price">' + priceBlock(state, data, C) + "</div>" +
        '<ul class="inc">' + listOf(C, "result.benefit.", 8).map(function (b) { return "<li>" + esc(b) + "</li>"; }).join("") + "</ul>" +
        countdown +
        '<button class="cta coral" id="buy">' + esc(C("result.buy")) + "</button>" +
        '<div class="pay">' + ((data && data.pay_methods) || []).map(function (p) { return "<span>" + esc(C("result.pay." + p)) + "</span>"; }).join("") + "</div>" +
        "</div></div>";
    }
  };

  // Question screens get a "n dari N" counter above the question; expecting parents skip
  // the screen-time question, so their N is one smaller.
  function render(step, state, data, C) {
    var fn = R[step];
    if (!fn) return "";
    var qs = questionsFor(state.goal === "hamil"), q = qs.indexOf(step);
    var counter = q === -1 ? "" : '<p class="counter">' + esc(fmt(C("ui.counter"), { n: q + 1, total: qs.length })) + "</p>";
    return counter + fn(state, data, C);
  }

  // ---------------------------------------------------------------------------
  // Browser controller: state, navigation, event binding, checkout handoff.
  // ---------------------------------------------------------------------------

  // The nextStep context for the current state: reviews are skipped when the
  // recommended kit has fewer than two filled in the content file.
  function stepCtx(state, data, C) {
    var s = state.stage && STAGES[state.stage.recIdx];
    var count = s && C ? stageReviews(s.id, C, data && data.img_base).length : 0;
    return { variant: state.variant, leadCaptured: !!state.lead, reviewCount: count, hamil: state.goal === "hamil" };
  }

  // Stand-in until assets/lt-quiz-track.js loads (or when it 404s): same surface, does nothing.
  function noopTracker() {
    var v = "A", f = function () {};
    try { v = window.localStorage.getItem("lt_quiz_v") || "A"; } catch (e) { /* storage blocked */ }
    return { init: f, event: f, flush: f, setContext: f, sessionId: "", variant: v };
  }

  var NO_BACK = ["intro", "goal", "ch1", "ch2", "loading"];
  var HAS_HISTORY = typeof history !== "undefined" && !!history.pushState;

  function later(app, fn, ms) { app.timers.push(setTimeout(fn, ms)); }

  function go(app, step) {
    app.history.push(step);
    var mode = HAS_HISTORY && entryMode(app.history);
    if (mode) history[mode + "State"]({ step: step }, "", location.href);
    show(app, step);
  }

  function goBack(app) {
    var b = backTarget(app.history);
    if (!b) return;
    app.history = b.history;
    show(app, b.step);
  }

  function next(app) { go(app, nextStep(app.state.step, stepCtx(app.state, app.data, app.C))); }

  function show(app, step) {
    app.timers.forEach(clearTimeout);
    app.timers = [];
    app.state.step = step;
    app.screen.innerHTML = render(step, app.state, app.data, app.C);
    app.back.hidden = NO_BACK.indexOf(step) !== -1;
    app.root.classList.toggle("inter", INTER_SCREENS.indexOf(step) !== -1 || step === "factors");
    window.scrollTo(0, 0);
    app.T.event("step_view", step);
    if (step === "loading" || step === "result") app.T.flush();
    bind(app, step);
  }

  function bind(app, step) {
    var st = app.state, T = app.T, C = app.C, $ = function (id) { return app.screen.querySelector("#" + id); };
    var all = function (sel) { return Array.prototype.slice.call(app.screen.querySelectorAll(sel)); };

    all("[data-next]").forEach(function (b) { b.onclick = function () { next(app); }; });

    all("[data-single]").forEach(function (b) {
      b.onclick = function () {
        var raw = b.getAttribute("data-single"), i = raw.indexOf(":");
        var group = raw.slice(0, i), key = raw.slice(i + 1);
        st[group] = group === "area" ? Number(key) : key;
        if (group === "goal" && key === "hamil") st.screen = null; // not asked before birth
        all("[data-single]").forEach(function (o) { o.setAttribute("aria-pressed", String(o === b)); });
        T.event("answer", step, group === "area" ? areaValue(Number(key)) : key);
        if (group === "time") T.event("quiz_complete", step, key);
        later(app, function () { next(app); }, 220);
      };
    });

    if (step === "dob") {
      $("dobNext").onclick = function () {
        var val = $("dob").value, err = $("err");
        if (!val) { err.textContent = C("q.dob.err.empty"); return; }
        var r = computeStage({ goal: st.goal, dob: val }, new Date());
        if (r.error) { err.textContent = C("q.dob.err." + r.error); return; }
        st.dob = val;
        st.stage = r;
        st.bundle = false; // the recommended pair may have changed
        T.setContext({ recommended_stage: STAGES[r.recIdx].id, age_months: r.months, goal: st.goal });
        T.event("answer", step, r.months === null ? "" : String(r.months));
        next(app);
      };
    }

    if (step === "hope") {
      all("[data-hope]").forEach(function (b) {
        b.onclick = function () {
          var k = b.getAttribute("data-hope"), h = st.hopes;
          if (k === "semua") h = h.indexOf("semua") !== -1 ? [] : HOPE_KEYS.slice();
          else h = h.filter(function (x) { return x !== "semua" && x !== k; }).concat(h.indexOf(k) !== -1 ? [] : [k]);
          st.hopes = h;
          all("[data-hope]").forEach(function (o) { o.setAttribute("aria-checked", String(h.indexOf(o.getAttribute("data-hope")) !== -1)); });
          $("hopeNext").disabled = !h.length;
        };
      });
      $("hopeNext").onclick = function () { T.event("answer", step, st.hopes.join(",")); next(app); };
    }

    if (step === "ch1" || step === "ch2") later(app, function () { next(app); }, 2500);

    if (step === "loading") {
      later(app, function () { var m = $("lmsg"); m.firstChild.textContent = m.getAttribute("data-good"); }, 2000);
      later(app, function () { next(app); }, 4200);
    }

    if (step === "gate") {
      var input = $("contact");
      $("gateNext").onclick = function () {
        var n = normaliseContact(input.value);
        if (!n) { $("gateErr").textContent = C("gate.err.invalid"); return; }
        T.event("lead_captured", "gate", n.method, { contact: n.contact });
        T.flush();
        st.lead = true;
        go(app, "result");
      };
      input.onkeydown = function (e) { if (e.key === "Enter") $("gateNext").click(); };
      later(app, function () { input.focus(); }, 50);
    }

    if (step === "result") {
      all("[data-offer]").forEach(function (b) {
        b.onclick = function () {
          st.bundle = b.getAttribute("data-offer") === "bundle";
          all("[data-offer]").forEach(function (o) { o.setAttribute("aria-checked", String(o === b)); });
          $("price").innerHTML = priceBlock(st, app.data, C);
        };
      });
      $("buy").onclick = function () {
        this.disabled = true;
        T.event("checkout_click", "result", st.bundle && bundleOffer(st, app.data) ? "bundle" : "single");
        T.flush();
        checkout(app);
      };
    }
  }

  // Add the kit (or both kits of the bundle) to the existing cart, never clearing it, tag
  // the cart for order attribution, go to checkout. If any cart call fails, fall back to the product page
  // so the parent can still buy.
  function checkout(app) {
    var st = app.state, kit = kitOf(app.data, STAGES[st.stage.recIdx]);
    var cart = cartPayload(st, app.data, !!st.bundle), attrs = { _lt_quiz_session: app.T.sessionId };
    Object.keys(cart.attributes).forEach(function (k) { attrs[k] = cart.attributes[k]; });
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    var post = function (path, body) {
      return fetch(root + path, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body || {}) })
        .then(function (r) { if (!r.ok) throw new Error(path + " " + r.status); return r.json(); });
    };
    return post("cart/add.js", { items: cart.items })
      .then(function () { return post("cart/update.js", { attributes: attrs }); })
      .then(function () { window.location.href = root + "checkout"; })
      .catch(function (e) {
        if (typeof console !== "undefined") console.error(e);
        window.location.href = kit.url || root + "cart";
      });
  }

  function mount(root) {
    var data = JSON.parse(document.getElementById("lt-quiz-data").textContent);
    return fetch(root.getAttribute("data-content-url"))
      .then(function (r) { return r.json(); })
      .then(function (content) {
        var T = window.LTTrack || noopTracker();
        var app = {
          root: root, screen: root.querySelector("#lt-screen"), back: root.querySelector("#lt-back"),
          data: data, C: makeC(content.items), T: T, history: [], timers: [],
          state: { step: null, variant: T.variant === "B" ? "B" : "A", goal: null, dob: "", area: null, conf: null,
            hopes: [], who: null, screen: null, time: null, stage: null, lead: false, bundle: false }
        };
        app.back.textContent = app.C("ui.nav.back");
        // With history, the button goes through the browser so its stack stays in step.
        app.back.onclick = function () { if (HAS_HISTORY) history.back(); else goBack(app); };
        if (HAS_HISTORY) window.addEventListener("popstate", function () { goBack(app); });
        T.init({ endpoint: data.endpoint });
        T.event("quiz_start");
        go(app, "intro");
        return app;
      })
      .catch(function (e) { if (typeof console !== "undefined") console.error("lt-quiz:", e); });
  }

  var LTQuiz = {
    STAGES: STAGES,
    AREA_KEYS: AREA_KEYS,
    GOAL_KEYS: GOAL_KEYS,
    CONF_KEYS: CONF_KEYS,
    HOPE_KEYS: HOPE_KEYS,
    WHO_KEYS: WHO_KEYS,
    SCREEN_KEYS: SCREEN_KEYS,
    TIME_KEYS: TIME_KEYS,
    FLOW: FLOW,
    QUESTION_SCREENS: QUESTION_SCREENS,
    INTER_SCREENS: INTER_SCREENS,
    SKIP_IF_HAMIL: SKIP_IF_HAMIL,
    monthsOld: monthsOld,
    computeStage: computeStage,
    nextStep: nextStep,
    questionsFor: questionsFor,
    backStep: backStep,
    entryMode: entryMode,
    backTarget: backTarget,
    normaliseContact: normaliseContact,
    areaValue: areaValue,
    fmt: fmt,
    makeC: makeC,
    urgency: urgency,
    stageReviews: stageReviews,
    bundleOffer: bundleOffer,
    cartPayload: cartPayload,
    checkout: checkout,
    R: R,
    render: render,
    stepCtx: stepCtx,
    mount: mount
  };

  if (typeof window !== "undefined") window.LTQuiz = LTQuiz;
  if (typeof module !== "undefined" && module.exports) module.exports = LTQuiz;

  if (typeof document !== "undefined") {
    var quizRoot = document.getElementById("lt-quiz");
    if (quizRoot) mount(quizRoot);
  }
})();
